import React, { useState } from "react"
import {
  Layout,
  Result,
  Button,
  Space,
  Typography,
  Input,
  Alert,
  Spin,
  Card,
} from "@mimber/ui-web"
import {
  useRequestReactivationCode,
  useConfirmReactivation,
} from "@mimber/client/mimber-organization"
import { useAuth } from "@/core/contexts"
import { message } from "@mimber/ui-web"
import type { AccountStatusGateProps } from "./AccountStatusGate.types"

const { Content } = Layout
const { Text, Title } = Typography

/**
 * AccountStatusGate - Blocks deactivated/deleted users and shows recovery UI
 *
 * Flow:
 * 1. For deactivated-only users: show status + request reactivation code
 * 2. For deleted users (grace period): show countdown + request reactivation code
 * 3. User enters 6-digit code → account reactivated → app proceeds
 */
export const AccountStatusGate: React.FunctionComponent<
  AccountStatusGateProps
> = ({ children, deactivated, deleted, deletedScheduledAt }) => {
  const { logout } = useAuth()
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState("")

  const requestCodeMutation = useRequestReactivationCode({
    onSuccess: () => {
      setCodeSent(true)
      message.success("Verification code sent to your email")
    },
    onError: (error) => {
      message.error(error.message || "Failed to send verification code")
    },
  })

  const confirmMutation = useConfirmReactivation({
    onSuccess: () => {
      message.success("Account reactivated successfully!")
      // Force page reload to restart the gate flow with fresh data
      window.location.reload()
    },
    onError: (error) => {
      message.error(error.message || "Invalid verification code")
    },
  })

  const isAccountBlocked = deactivated || deleted

  if (!isAccountBlocked) {
    return <>{children}</>
  }

  const isPendingDeletion = !!deleted
  const daysRemaining = deletedScheduledAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(deletedScheduledAt).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        )
      )
    : null

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Content
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          background: "var(--color-background)",
        }}
      >
        <Card style={{ maxWidth: 480, width: "100%" }}>
          <Result
            status={isPendingDeletion ? "error" : "warning"}
            title={
              isPendingDeletion
                ? "Account Scheduled for Deletion"
                : "Account Deactivated"
            }
            subTitle={
              isPendingDeletion
                ? daysRemaining !== null
                  ? `Your account will be permanently deleted in ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""}. Reactivate now to cancel deletion.`
                  : "Your account is scheduled for permanent deletion. Reactivate now to cancel."
                : "Your account has been deactivated. Reactivate your account to continue."
            }
          />

          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            {!codeSent ? (
              <>
                <Alert
                  type="info"
                  showIcon
                  description="We'll send a 6-digit verification code to your email address to confirm your identity."
                />
                <Button
                  type="primary"
                  block
                  loading={requestCodeMutation.isPending}
                  onClick={() => requestCodeMutation.mutate()}
                >
                  Send Reactivation Code
                </Button>
              </>
            ) : (
              <>
                <Alert
                  type="success"
                  showIcon
                  description="A verification code has been sent to your email. Enter it below to reactivate your account."
                />

                <div>
                  <Title level={5} style={{ marginBottom: 8 }}>
                    Enter Verification Code
                  </Title>
                  <Input
                    size="large"
                    placeholder="000000"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    style={{
                      textAlign: "center",
                      letterSpacing: "8px",
                      fontSize: "24px",
                      fontFamily: "monospace",
                    }}
                  />
                </div>

                <Space style={{ width: "100%" }}>
                  <Button
                    type="primary"
                    block
                    disabled={code.length !== 6}
                    loading={confirmMutation.isPending}
                    onClick={() => confirmMutation.mutate({ code })}
                  >
                    {confirmMutation.isPending ? (
                      <Spin size="small" />
                    ) : (
                      "Reactivate Account"
                    )}
                  </Button>
                </Space>

                <Text type="secondary" style={{ textAlign: "center" }}>
                  Didn't receive the code?{" "}
                  <Button
                    type="link"
                    size="small"
                    disabled={requestCodeMutation.isPending}
                    onClick={() => {
                      setCode("")
                      requestCodeMutation.mutate()
                    }}
                    style={{ padding: 0 }}
                  >
                    Resend
                  </Button>
                </Text>
              </>
            )}

            <Button
              type="default"
              block
              onClick={() => logout()}
              style={{ marginTop: 8 }}
            >
              Sign Out
            </Button>
          </Space>
        </Card>
      </Content>
    </Layout>
  )
}
